import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Modal from "@mui/material/Modal";
import closeLogoBlack from "../assets/images/chats/closeLogoBlack.svg";
import { CloseButton, CloseIcon } from "../page/Profille/CustomDrawer";
import profile_background_logo from "../assets/images/Network.svg";
import linkedin from "../assets/images/postcard/Linkedin.svg";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import PersonIcon from "../assets/images/chats/person.svg";
import { createStyledIndicatorButton } from "./StyledComponents/SideBarComponents";
import ProfileOwnerDetailModal from "./ProfileOwnerDetailModal";
import group_icon from "../assets/images/chats/group_icon.svg";
import useAppSelectors from "../customHooks/useAppSelectors";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  dropDeletedGroup,
  handleExitFromGroup,
  handleDeactivatePrivateChat,
  requestDeleteGroupForTeam,
  requestEditGroupName,
} from "../appStore/actions/ArkchatGroup/action";
import { handleSwitchAccountRefresh } from "../appStore/actions/AppState/action";
import { showToastMsg } from "../appStore/actions/global/action";
import { GroupLevelArkchatPermission, UI_FEATURES } from "../utils/constant";
import { canManageGroup } from "../utils/helper";
import InputDialogComponent from "../components/InputDialog";
import AddMemberGroupDrawer from "../page/chat/Drawer/AddMemberGroupDrawer";

const groupActionButtonStyle = {
  color: "black",
  borderColor: "rgba(0, 0, 0, 1)",
  borderRadius: 0,
  textTransform: "none",
  fontSize: "0.8rem",
  "&:hover": {
    borderColor: "rgba(0, 0, 0, 1)",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.35)",
    borderColor: "rgba(0, 0, 0, 0.25)",
  },
};

const style = {
  position: "absolute",
  top: "40%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "323px",
  bgcolor: "background.paper",
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.30)",
};

const PictureBox = ({ profileImgUrl, coverImgUrl, initialName = "" }) => (
  <Box maxHeight="8rem" height="8rem" width="100%" position="relative">
    <Box height="100%" overflow="hidden">
      <img
        src={coverImgUrl || profile_background_logo}
        alt="coverImg"
        style={{ width: "100%", height: "100%" }}
      />
    </Box>

    <Box
      bgcolor="#EAEAEA"
      position="absolute"
      left="50%"
      marginLeft="-50px"
      transform="translateX(-40%)"
      bottom="-20%"
      borderRadius="0.2rem"
      overflow="hidden"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100px"
        width="100px"
        padding={profileImgUrl ? "0px" : "15px"}
      >
        {profileImgUrl ? (
          <img
            src={profileImgUrl}
            alt="profileImg"
            style={{ width: "100%", height: "100%", objectFit: "fill" }}
          />
        ) : (
          <Typography fontSize="1.8rem">
            {initialName && initialName[0]}
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

const ProfileDetailModal = ({
  isProfileViewOpen,
  setProfileViewModal,
  profileViewData,
  setProfileViewData,
}) => {
  const { t } = useTranslation();
  const appContent = t("AppContent", { returnObjects: true });
  const SmallActionButton = createStyledIndicatorButton("#e6e3e3ff");
  const { currentUserData, selectedGroup, groupmemberdetails, switchAccountRefresh, groupList, archiveGroupList } =
    useAppSelectors();
  const dispatch = useDispatch();

  const [isProfileOwnerOpen, setIsProfileOwnerOpen] = useState(false);
  const [viewType, setViewType] = useState("owner");

  const [isAddMemberOpen, setAddMemberOpen] = useState(false);
  const [isConvertDialogOpen, setConvertDialogOpen] = useState(false);
  const [isPrivateDisableChat, setPrivateDisableChat] = useState(false);
  const [isExitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [isEditNameOpen, setEditNameOpen] = useState(false);
  const [isDeleteGroupOpen, setDeleteGroupOpen] = useState(false);
  const [deleteGroupInput, setDeleteGroupInput] = useState("");
  const [isDeletingGroup, setDeletingGroup] = useState(false);
  // Only the account owner deletes a group (one of their own account's),
  // for the whole team.
  const canDeleteGroup = Boolean(
    UI_FEATURES.deleteGroupForTeam && currentUserData?.accountOwner && !selectedGroup?.isExternal
  );
  const [groupNameInput, setGroupNameInput] = useState("");

  const handleClose = () => {
    setProfileViewModal(false);
    setProfileViewData("");
  };

  // Group Information follows edits made elsewhere (another member, or this
  // user on another device): the group-setting-updated signal updates the
  // open group and the chat lists, and this copies the new name/description.
  const viewedGroupId = profileViewData?.isGroup ? profileViewData?.groupId : undefined;
  useEffect(() => {
    if (!isProfileViewOpen || !viewedGroupId || !setProfileViewData) return;
    const live =
      (selectedGroup?.id === viewedGroupId && selectedGroup) ||
      groupList?.find((g) => g?.id === viewedGroupId) ||
      archiveGroupList?.find((g) => g?.id === viewedGroupId);
    if (!live) return;
    const groupName = live.name || profileViewData.groupName;
    const description = live.description ?? profileViewData.description;
    if (groupName !== profileViewData.groupName || description !== profileViewData.description) {
      setProfileViewData({ ...profileViewData, groupName, description });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfileViewOpen, viewedGroupId, selectedGroup, groupList, archiveGroupList]);

  const hasExitedGroup = Boolean(
    groupmemberdetails?.internalMember?.length > 0 &&
      groupmemberdetails?.internalMember.find(
        (item) =>
          item?.userId === currentUserData?.id &&
          item?.permissionType === GroupLevelArkchatPermission?.REMOVE
      )
  );

  const handleConfirmExitGroup = () => {
    dispatch(handleExitFromGroup({ group_id: selectedGroup?.id })).then(
      (res) => {
        setExitConfirmOpen(false);
        handleClose();
        dispatch(showToastMsg(res?.message));
      }
    );
  };

  const handleConfirmConvertToSimpleChat = async () => {
    const reqBody = {
      is_task_on: !isPrivateDisableChat,
      is_private_chat_on: !isPrivateDisableChat,
    };
    await dispatch(handleDeactivatePrivateChat(reqBody, selectedGroup?.id));
    setConvertDialogOpen(false);
  };

  const handleOpenEditName = () => {
    setGroupNameInput(profileViewData?.groupName || "");
    setEditNameOpen(true);
  };

  const handleConfirmEditName = async () => {
    if (!groupNameInput?.trim()) {
      return;
    }
    const reqBody = {
      group_name: groupNameInput,
      description: selectedGroup?.description,
    };
    await dispatch(requestEditGroupName(selectedGroup?.id, reqBody));
    setProfileViewData({ ...profileViewData, groupName: groupNameInput });
    setEditNameOpen(false);
  };

  const closeDeleteGroup = () => {
    setDeleteGroupOpen(false);
    setDeleteGroupInput("");
  };

  const handleConfirmDeleteGroup = async () => {
    if (deleteGroupInput !== "DELETE" || isDeletingGroup) return;
    const group = selectedGroup;
    setDeletingGroup(true);
    const res = await dispatch(requestDeleteGroupForTeam(group?.id));
    setDeletingGroup(false);
    if (!res?.status) return;
    closeDeleteGroup();
    handleClose();
    // The group-deleted signal may have done this already; only the first
    // moves the chat off the group.
    const wasListed = await dispatch(dropDeletedGroup({ pnId: group?.pnId, groupId: group?.id }));
    if (wasListed) dispatch(handleSwitchAccountRefresh(!switchAccountRefresh));
  };

  const handleAddMemberClick = () => {
    handleClose();
    setAddMemberOpen(true);
  };

  // open owner or group modal dynamically
  const handleOwnerDetail = (type) => {
    setViewType(type); // "owner" or "group"
    setIsProfileOwnerOpen(true);
  };

  const ownerDetails = profileViewData?.ownerData || {
    companyName: profileViewData?.companyName,
    cityName: profileViewData?.cityName,
    countryName: profileViewData?.countryName,
    industryName: profileViewData?.industry,
    website: profileViewData?.website,
    linkedin: profileViewData?.linkedin,
    coverImgUrl: profileViewData?.coverImgUrl,
    profileImgUrl: profileViewData?.profileImgUrl,
  };

  const groupDetails = profileViewData?.groupData || {
    userName: profileViewData?.groupOwner,
    email: profileViewData?.email,
  };

  return (
    <div>
      {/* Shared owner/group detail modal */}
      <ProfileOwnerDetailModal
        ownerData={ownerDetails}
        groupData={groupDetails}
        viewType={viewType}
        open={isProfileOwnerOpen}
        onClose={() => setIsProfileOwnerOpen(false)}
      />

      <AddMemberGroupDrawer
        isaddmemberOpen={isAddMemberOpen}
        handleCloseAddPeopleDrawer={() => setAddMemberOpen(false)}
      />

      {isConvertDialogOpen && (
        <InputDialogComponent
          title="Deactivate private chats & To Dos"
          isType="disableprivatechat"
          open={isConvertDialogOpen}
          handleClose={() => setConvertDialogOpen(false)}
          value={isPrivateDisableChat}
          handleDisableEnablepvtChat={(e) =>
            setPrivateDisableChat(e.target.checked)
          }
          btnLabel="Save"
          onClick={handleConfirmConvertToSimpleChat}
        />
      )}

      {isExitConfirmOpen && (
        <InputDialogComponent
          title="Exit Group"
          isType="exitgroup"
          groupName={profileViewData?.groupName}
          open={isExitConfirmOpen}
          handleClose={() => setExitConfirmOpen(false)}
          btnLabel="Yes, Exit Group"
          onClick={handleConfirmExitGroup}
          alternateBtnLabel="Cancel"
          handleAlternateBtnClick={() => setExitConfirmOpen(false)}
        />
      )}

      {isDeleteGroupOpen && (
        <InputDialogComponent
          title="Delete Group"
          isType="delete"
          open={isDeleteGroupOpen}
          handleClose={closeDeleteGroup}
          onChange={(e) => setDeleteGroupInput(e.target.value)}
          value={deleteGroupInput}
          btnLabel="Delete"
          disabled={deleteGroupInput !== "DELETE"}
          isLoading={isDeletingGroup}
          onClick={handleConfirmDeleteGroup}
        />
      )}

      {isEditNameOpen && (
        <InputDialogComponent
          title="Edit Group Name"
          isType="edit"
          open={isEditNameOpen}
          handleClose={() => setEditNameOpen(false)}
          onChange={(e) => setGroupNameInput(e.target.value)}
          value={groupNameInput}
          inputLable="Group Name"
          btnLabel="Update"
          onClick={handleConfirmEditName}
        />
      )}

      <Modal open={isProfileViewOpen} onClose={handleClose}>
        {profileViewData?.isGroup ? (
          <Box sx={style} textAlign="center">
            {/* Header */}
            <Box
              display="flex"
              alignItems="center"
              gap="0.4rem"
              padding="0.5rem"
            >
              <CloseButton
                onClick={handleClose}
                size="small"
                variant="contained"
              >
                <CloseIcon src={closeLogoBlack} alt="close" />
              </CloseButton>

              <Typography color="#000" fontSize="1rem" fontWeight={"16px"}>
                {"Group Information"}
              </Typography>
            </Box>

            {/* Group Info */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              padding="20px"
              fontFamily="Inter, sans-serif"
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                height="52px"
                width="52px"
                p="12px"
                sx={{
                  backgroundColor: "rgba(243, 243, 243, 1)",
                  borderRadius: "7px",
                  objectFit: "contain",
                  boxShadow: "0px 2.27px 5.27px 0px rgba(0, 0, 0, 0.25)",
                }}
              >
                <img
                  src={group_icon}
                  alt="profileImg"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap="0.3rem" mt={1.3}>
                <Typography fontWeight={600} fontSize="16px">
                  {profileViewData?.groupName}
                </Typography>
                {canManageGroup(currentUserData, selectedGroup?.permissionType, selectedGroup?.isExternal) && (
                  <IconButton
                    size="small"
                    onClick={handleOpenEditName}
                    sx={{ padding: "2px" }}
                  >
                    <EditIcon sx={{ fontSize: "0.9rem" }} />
                  </IconButton>
                )}
              </Box>

              <Typography fontSize="14px" mt={0.5} mb={1}>
                {profileViewData?.memberCount} Members
              </Typography>

              <Typography fontSize="14px" mb={2} lineHeight={1.4}>
                {profileViewData?.description ||
                  "A chat group to explore Arkchat features and collaboration tools."}
              </Typography>

              {/* Group Owner + Creator Info */}
              <Box
                alignItems="center"
                justifyContent="flex-start"
                gap={3}
                mb={1.5}
                ml={-5}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <SmallActionButton
                    size="small"
                    variant="contained"
                    sx={{ color: "black", width: "28px" }}
                    onClick={() => handleOwnerDetail("owner")}
                  >
                    {profileViewData?.groupOwner
                      ? profileViewData.groupOwner.charAt(0).toUpperCase()
                      : "?"}
                  </SmallActionButton>

                  <Typography
                    fontSize="13px"
                    textAlign="left"
                    fontFamily="Poppins"
                  >
                    Group Owner: {profileViewData?.companyName || "-"}
                  </Typography>
                </Box>

                <Box display="flex" gap={1} mt={1}>
                  <SmallActionButton
                    size="small"
                    variant="contained"
                    onClick={() => handleOwnerDetail("group")}
                    sx={{ width: "28px" }}
                  >
                    <img
                      style={{ width: "100%", height: "100%" }}
                      src={PersonIcon}
                      alt="group"
                    />
                  </SmallActionButton>

                  <Typography
                    fontSize="13px"
                    textAlign="left"
                    fontFamily="Poppins"
                  >
                    Created by {profileViewData?.groupOwner || "-"} <br />
                    {moment(profileViewData?.createdDate).format(currentUserData?.userDateFormat) || "-"}
                  </Typography>
                </Box>
              </Box>

              <Typography fontSize="13px" mb={1}>
                You joined this group on {moment(profileViewData?.joinedDate).format(currentUserData?.userDateFormat)}
              </Typography>

              <Box display="flex" flexDirection="column" gap="0.6rem" width="100%" mt={1}>
                {canManageGroup(currentUserData, selectedGroup?.permissionType, selectedGroup?.isExternal) && (
                  <Button
                    fullWidth
                    variant="outlined"
                    disableElevation
                    onClick={() => setConvertDialogOpen(true)}
                    sx={groupActionButtonStyle}
                  >
                    Convert to Simple Chat Group
                  </Button>
                )}

                <Box display="flex" gap="0.6rem">
                  <Button
                    fullWidth
                    variant="outlined"
                    disableElevation
                    disabled={hasExitedGroup}
                    onClick={() => setExitConfirmOpen(true)}
                    sx={groupActionButtonStyle}
                  >
                    Exit Group
                  </Button>
                  {canManageGroup(currentUserData, selectedGroup?.permissionType, selectedGroup?.isExternal) && (
                    <Button
                      fullWidth
                      variant="outlined"
                      disableElevation
                      onClick={handleAddMemberClick}
                      sx={groupActionButtonStyle}
                    >
                      Add Member
                    </Button>
                  )}
                </Box>
                {canDeleteGroup && (
                  <Button
                    fullWidth
                    variant="outlined"
                    disableElevation
                    onClick={() => setDeleteGroupOpen(true)}
                    sx={{ ...groupActionButtonStyle, color: "#DB0011", borderColor: "#DB0011", "&:hover": { borderColor: "#DB0011", backgroundColor: "rgba(219, 0, 17, 0.04)" } }}
                  >
                    Delete Group
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          // User Profile Layout
          <Box sx={style} overflow="hidden">
            <Box
              display="flex"
              alignItems="center"
              gap="0.4rem"
              bgcolor="rgba(234, 234, 234, 1)"
              padding="0.5rem"
            >
              <CloseButton
                onClick={handleClose}
                size="small"
                variant="contained"
              >
                <CloseIcon src={closeLogoBlack} alt="close" />
              </CloseButton>

              <Typography color="#000" fontSize="1rem" fontWeight={600}>
                {appContent?.KEY14 || "Profile"}
              </Typography>
            </Box>

            {!profileViewData?.userName && (
              <Box
                zIndex={2011}
                position="absolute"
                display="flex"
                alignItems="center"
                height="95%"
                width="100%"
                justifyContent="center"
                bgcolor="rgba(0, 0, 0, 0.5)"
              >
                <CircularProgress sx={{ color: "#DB0011" }} />
              </Box>
            )}

            <Box position="relative">
              <PictureBox
                profileImgUrl={profileViewData?.profileImgUrl}
                coverImgUrl={profileViewData?.coverImgUrl}
                initialName={profileViewData?.userName}
              />

              {profileViewData?.linkedin && (
                <Box
                  sx={{ cursor: "pointer" }}
                  position="absolute"
                  right="8rem"
                  bottom="-1.6rem"
                >
                  <Link
                    href={
                      profileViewData.linkedin.startsWith("http")
                        ? profileViewData.linkedin
                        : `https://${profileViewData.linkedin}`
                    }
                    target="_blank"
                    rel="noopener"
                  >
                    <img src={linkedin} title="Linkedin" alt="linkedin" />
                  </Link>
                </Box>
              )}
            </Box>

            <Box mt="2rem" textAlign="center" padding="0.5rem 0" mb="1rem">
              <Typography fontWeight={600}>
                {profileViewData?.userName}
              </Typography>
              <Typography>{profileViewData?.companyName}</Typography>

              {profileViewData?.website && (
                <Link
                  href={
                    profileViewData.website.startsWith("http")
                      ? profileViewData.website
                      : `https://${profileViewData.website}`
                  }
                  target="_blank"
                  rel="noopener"
                >
                  <Typography
                    fontSize="0.88rem"
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    {profileViewData.website}
                  </Typography>
                </Link>
              )}

              <Typography>
                {profileViewData?.industryName} <br /> {profileViewData?.cityName}, {profileViewData?.countryName}
              </Typography>

              {profileViewData?.profileBrief && (
                <Box mt="1rem" margin="auto">
                  <Typography sx={{ wordBreak: "break-word" }} width="100%">
                    {profileViewData.profileBrief}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default ProfileDetailModal;
